import { cn } from "@/lib/cn";
import { type CSSProperties, forwardRef } from "react";

export interface AvatarProps {
  /** Display name — first character used as initial */
  name?: string;
  /** Override background color (otherwise deterministic from name) */
  color?: string;
  /** Diameter in px */
  size?: number;
  className?: string;
  style?: CSSProperties;
}

const PALETTE = [
  "var(--iris-neon-purple)",
  "var(--iris-neon-pink)",
  "var(--iris-neon-cyan)",
  "var(--iris-neon-green)",
  "var(--iris-neon-orange)",
  "var(--iris-neon-blue)",
  "var(--iris-neon-magenta)",
  "var(--iris-neon-yellow)",
];

/**
 * Deterministic color hash: same name always produces the same palette slot.
 */
function avatarColorFor(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  const idx = h % PALETTE.length;
  return PALETTE[idx] as string;
}

/**
 * Circular avatar with a single initial and deterministic background color.
 * Uses the iris-avatar CSS class for base styling.
 */
export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(
  ({ name = "?", color, size = 28, className, style }, ref) => {
    const bg = color ?? avatarColorFor(name);
    const initial = (name || "?").trim()[0]?.toUpperCase() || "?";
    return (
      <span
        ref={ref}
        className={cn("iris-avatar", className)}
        style={{
          width: size,
          height: size,
          background: bg,
          fontSize: size * 0.42,
          ...style,
        }}
      >
        {initial}
      </span>
    );
  },
);
Avatar.displayName = "Avatar";
